/**
 * Location Store
 *
 * Zustand store for managing the current location status
 * Used across the app to track where the user/dog currently is
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PlaceType } from '@/shared/types/api.types';

// Frontend location type (used in UI)
export type LocationType = 'home' | 'street' | 'daycare' | 'apt';

// Daycare/Hotel stay information
export interface DaycareStay {
  checkInDate: string; // ISO date string
  checkInTime: string; // HH:mm format
  checkOutDate: string; // ISO date string
  checkOutTime: string; // HH:mm format
  establishmentName?: string;
}

// Map frontend location to API PlaceType
export function mapLocationToPlaceType(location: LocationType): PlaceType {
  const mapping: Record<LocationType, PlaceType> = {
    home: 'HOME',
    apt: 'APARTMENT',
    street: 'STREET',
    daycare: 'HOTEL',
  };
  return mapping[location];
}

// Map API PlaceType to frontend location
export function mapPlaceTypeToLocation(place: PlaceType): LocationType {
  const mapping: Record<PlaceType, LocationType> = {
    HOME: 'home',
    APARTMENT: 'apt',
    STREET: 'street',
    HOTEL: 'daycare',
  };
  return mapping[place];
}

interface LocationState {
  location: LocationType;
  daycareStay: DaycareStay | null;
  setLocation: (location: LocationType) => void;
  setDaycareStay: (stay: DaycareStay | null) => void;
  clearDaycareStay: () => void;
  getPlaceType: () => PlaceType;
  isDaycareActive: () => boolean;
}

export const useLocationStore = create<LocationState>()(
  persist(
    (set, get) => ({
      location: 'home',
      daycareStay: null,
      setLocation: (location) => set({ location }),
      setDaycareStay: (stay) => set({ daycareStay: stay }),
      clearDaycareStay: () => set({ daycareStay: null, location: 'home' }),
      getPlaceType: () => mapLocationToPlaceType(get().location),
      isDaycareActive: () => {
        const state = get();
        if (state.location !== 'daycare' || !state.daycareStay) return false;

        const now = new Date();
        const checkOut = new Date(`${state.daycareStay.checkOutDate}T${state.daycareStay.checkOutTime}`);

        return now <= checkOut;
      },
    }),
    {
      name: 'aiodog-location',
    }
  )
);
